import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { ListQuery, PaginatedResponse, SortDirection } from '../../../types/access'
import { useAdminListState, type ListFilterValue, type ListViewState } from '../../../context/AdminListStateContext'

const debounceMs = 300
const stableQueryKey = (query: Record<string, unknown>) => JSON.stringify(
  Object.keys(query).sort().map(key => [key, query[key]]),
)

export function useDebouncedValue<T>(value: T, delay = debounceMs) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay)
    return () => window.clearTimeout(timer)
  }, [delay, value])
  return debounced
}

export function usePaginatedList<T, F extends Record<string, ListFilterValue> = Record<string, ListFilterValue>>(options: {
  defaultSortBy: string
  defaultSortDir?: SortDirection
  defaultFilters?: F
  enabled?: boolean
  /** Opt-in return navigation memory within the current verified Admin scope. */
  stateKey?: string
  /** Identity of a client-side data source; replacing it invalidates old results. */
  source?: unknown
  normalizeView?: (view: ListViewState) => ListViewState
  queryFilterKeys?: readonly string[]
  fetchPage: (query: ListQuery & F, signal?: AbortSignal) => Promise<PaginatedResponse<T>>
}) {
  const scope = useAdminListState()
  const emptyItems = useMemo<T[]>(() => [], [])
  const enabled = (options.enabled ?? true) && (scope === null || scope.scopeKey !== null)
  const fetchPageRef = useRef(options.fetchPage)
  fetchPageRef.current = options.fetchPage
  const [rawView, setRawView] = useState<ListViewState>(() =>
    (options.stateKey && scope?.views.get(options.stateKey)) || {
      page: 1, pageSize: 25, search: '', sortBy: options.defaultSortBy,
      sortDir: options.defaultSortDir || 'asc', filters: { ...options.defaultFilters },
    },
  )
  const normalize = options.normalizeView || ((value: ListViewState) => value)
  const view = useMemo(() => normalize(rawView), [rawView, options.normalizeView])
  const setView = (update: (previous: ListViewState) => ListViewState) => setRawView(previous => normalize(update(normalize(previous))))
  const { page, pageSize, search, sortBy, sortDir } = view
  const filters = view.filters as F
  const debouncedSearch = useDebouncedValue(search)
  const queryCandidate = { ...Object.fromEntries(Object.entries(filters).filter(([key]) => !options.queryFilterKeys || options.queryFilterKeys.includes(key))), page, pageSize,
    search: debouncedSearch || undefined, sortBy, sortDir,
  } as ListQuery & F
  const queryKey = stableQueryKey(queryCandidate)
  // Page-only refinements must not refetch an unchanged source query.
  const query = useMemo(() => queryCandidate, [queryKey])
  // Raw search invalidates immediately, including the debounce window. Source
  // replacement and scope/enable changes must also hide previous records now.
  const identity = useMemo(() => ({}), [queryKey, search, enabled, scope, options.source])
  const currentIdentity = useRef<object | null>(identity)
  currentIdentity.current = identity
  const mounted = useRef(false)
  const request = useRef({ generation: 0, controller: null as AbortController | null })
  const [result, setResult] = useState<{
    identity: object; data: PaginatedResponse<T> | null; loading: boolean; error: string
  } | null>(null)

  useLayoutEffect(() => {
    mounted.current = true
    currentIdentity.current = identity
    return () => {
      mounted.current = false
      currentIdentity.current = null
      request.current.generation++
      request.current.controller?.abort()
    }
  }, [])
  useLayoutEffect(() => {
    if (enabled && options.stateKey && scope) scope.views.set(options.stateKey, view)
  }, [enabled, options.stateKey, scope, view])

  const refresh = useCallback(async () => {
    if (!enabled || search !== debouncedSearch || !mounted.current || currentIdentity.current !== identity) return false
    request.current.controller?.abort()
    const controller = new AbortController()
    const generation = ++request.current.generation
    request.current.controller = controller
    const current = () => mounted.current && currentIdentity.current === identity
      && request.current.generation === generation && !controller.signal.aborted
    setResult(previous => ({ identity, data: previous?.identity === identity ? previous.data : null, loading: true, error: '' }))
    try {
      const next = await fetchPageRef.current(query, controller.signal)
      if (!current()) return false
      setResult({ identity, data: next, loading: false, error: '' })
      if (next.pagination.total > 0 && next.pagination.page < page) {
        setView(previous => ({ ...previous, page: next.pagination.page }))
      }
      return true
    } catch (err: any) {
      if (!current()) return false
      // A denied/failed refresh must not leave previously loaded records visible.
      setResult({ identity, data: null, loading: false,
        error: err?.response?.data?.message || err?.message || 'Unable to load list.' })
      return false
    }
  }, [enabled, identity, search, debouncedSearch, query, page])

  // Preserve the established void-returning API; commands can inspect the same
  // guarded operation's outcome without duplicating fetch/error behavior.
  const refreshWithoutResult = useCallback(async () => { await refresh() }, [refresh])

  useEffect(() => {
    void refresh()
    return () => { request.current.controller?.abort() }
  }, [refresh])

  const updateSearch = (value: string) => setView(previous => previous.search === value ? previous : { ...previous, search: value, page: 1 })
  const updateFilter = <K extends keyof F>(key: K, value: F[K], resetPage = true) => setView(previous =>
    previous.filters[key as string] === value ? previous : { ...previous, filters: { ...previous.filters, [key]: value }, page: resetPage ? 1 : previous.page })
  const updateSort = (key: string) => setView(previous => ({ ...previous, page: 1, sortBy: key,
    sortDir: previous.sortBy === key && previous.sortDir === 'asc' ? 'desc' : 'asc',
  }))
  const updatePageSize = (value: number) => setView(previous => previous.pageSize === value ? previous : { ...previous, pageSize: value, page: 1 })
  const data = enabled && result?.identity === identity ? result.data : null
  return {
    items: data?.items || emptyItems,
    pagination: data?.pagination || { page, pageSize, total: 0, totalPages: 1, hasNextPage: false, hasPreviousPage: page > 1 },
    loading: enabled && (result?.identity !== identity || result.loading),
    error: enabled && result?.identity === identity ? result.error : '',
    query, search, sortBy, sortDir, filters, view,
    applyView: (next: ListViewState) => setView(() => ({ ...next, page: 1 })),
    setPage: (value: number) => setView(previous => ({ ...previous, page: value })),
    setPageSize: updatePageSize, setSearch: updateSearch, setFilter: updateFilter, setSort: updateSort,
    setSorting: (key: string, direction: SortDirection) => setView(previous => previous.sortBy === key && previous.sortDir === direction ? previous : { ...previous, page: 1, sortBy: key, sortDir: direction }),
    resetFilters: () => setView(previous => ({ ...previous, page: 1, search: '', filters: { ...options.defaultFilters } })),
    refresh: refreshWithoutResult, refreshResult: refresh,
  }
}
